import { Axes } from "./core/elements/axes"
import { Axis } from "./core/elements/axis"
import { Format } from "./core/elements/format"
import { Legend } from "./core/elements/legend"
import { Panel } from "./core/elements/panel"
import { Scroll } from "./core/elements/scroll"
import { Wall } from "./core/elements/wall"
import { Zoom } from "./core/elements/zoom"
import { Point, Rectangle } from "./core/primitives"
import { Palette } from "./palette"
import { SeriesList } from "./series"
import { Title } from "./tools/index"
import { doubleTap } from "./utils"

/**
 * Zoom the axes of the chart changing the min and the max value when two fingers are touching and moving in the canvas.
 */

function twoFingersZoom(chart, zoom) {
	var canvas = chart.canvas
	var timer
	var maxDistX = 0,
		minDistX = 0,
		maxDistY = 0,
		minDistY = 0
	var newMinDistX = 0,
		newMaxDistX = 0,
		newMinDistY = 0,
		newMaxDistY = 0
	var oldMinDistX = 0,
		oldMaxDistX = 0,
		oldMinDistY = 0,
		oldMaxDistY = 0
	var touches = []
	var touchedMoreThanOnceStart
	var pMinX, pMinY, pMaxX, pMaxY
	var iniMinX, iniMinY, iniMaxX, iniMaxY
	touchedMoreThanOnceStart = false
	timer = setInterval(touchZoom, 100)

	function touchZoom() {
		var len = touches.length
		if (len > 1) {
			var tmp = 0
			var touch1 = touches[0]
			var touch2 = touches[1]
			zoom.touching = true
			if (!touchedMoreThanOnceStart) {
				iniMinX = touch1.pageX
				iniMaxX = touch2.pageX
				iniMinY = touch1.pageY
				iniMaxY = touch2.pageY

				if (iniMinX > iniMaxX) {
					tmp = iniMinX
					iniMinX = iniMaxX
					iniMaxX = tmp
				}
				if (iniMinY > iniMaxY) {
					tmp = iniMinY
					iniMinY = iniMaxY
					iniMaxY = tmp
				}

				touchedMoreThanOnceStart = true
			} else {
				pMinX = touch1.pageX
				pMaxX = touch2.pageX
				pMinY = touch1.pageY
				pMaxY = touch2.pageY
				if (pMinX > pMaxX) {
					tmp = pMinX
					pMinX = pMaxX
					pMaxX = tmp
				}
				if (pMinY > pMaxY) {
					tmp = pMinY
					pMinY = pMaxY
					pMaxY = tmp
				}

				newMinDistX = pMinX - iniMinX
				newMinDistY = pMinY - iniMinY
				newMaxDistX = pMaxX - iniMaxX
				newMaxDistY = pMaxY - iniMaxY

				minDistX = oldMinDistX - newMinDistX
				minDistY = oldMinDistY - newMinDistY
				maxDistX = oldMaxDistX - newMaxDistX
				maxDistY = oldMaxDistY - newMaxDistY

				oldMinDistX = newMinDistX
				oldMinDistY = newMinDistY
				oldMaxDistX = newMaxDistX
				oldMaxDistY = newMaxDistY
			}
			if (zoom.direction == "both" || zoom.direction == "horizontal") {
				drawMinMaxBottom(chart.axes.bottom.startPos + minDistX, chart.axes.bottom.endPos + maxDistX)
			}
			if (zoom.direction == "both" || zoom.direction == "vertical") {
				drawMinMaxLeft(chart.axes.left.startPos + minDistY, chart.axes.left.endPos + maxDistY)
			}
		}
	}

	canvas.addEventListener("touchend", () => {
		if (touchedMoreThanOnceStart) {
			touchedMoreThanOnceStart = false
		}
		clearInterval(timer)
	})
	canvas.addEventListener("touchmove", event => {
		event.preventDefault()
		touches = event.touches
	})

	function drawMinMaxBottom(min, max) {
		chart.axes.top.calcMinMax(min, max)
		chart.axes.bottom.calcMinMax(min, max)
		chart.draw()
	}
	function drawMinMaxLeft(min, max) {
		chart.axes.right.calcMinMax(min, max)
		chart.axes.left.calcMinMax(min, max)
		chart.draw()
	}
}

/**
 * @memberOf Tee.Chart
 * @constructor
 * @class Contains a list with all "tools"
 * @param {Tee.Chart} chart The parent chart this tool list belongs to.
 * @property {Tool[]} items Array of Tool objects.
 */
function Tools(chart) {
	this.chart = chart
	this.items = []

	this.draw = function () {
		for (var t = 0, s, len = this.items.length; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.active)
				if (s instanceof Tee.ToolTip) {
					if (s.currP != undefined && s.realTime) s.mousemove(s.currP)
					else s.draw()
				} else s.draw()
		}
	}

	this.mousemove = function (p) {
		for (var t = 0, s, len = this.items.length; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.active) {
				s.currP = undefined
				s.mousemove(p)
			}
		}
	}

	this.mousedown = function (event) {
		for (var t = 0, s, len = this.items.length, done = false; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.active) {
				if (s.mousedown(event)) done = true
			}
		}

		return done
	}

	this.mouseout = function () {
		for (var t = 0, s, len = this.items.length; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.active) s.mouseout()
		}
	}

	this.clicked = function (p) {
		var l = this.items.length

		for (var t = l - 1, s, done = false; t >= 0; t--) {
			s = this.items[t]
			if (!s) break
			if (s.active && s.clicked(p)) {
				done = true

				if (s.onclick) done = s.onclick(s, p.x, p.y)
			}
		}

		return done
	}

	/**
	 * @returns {Tool} Returns the tool parameter.
	 */
	this.add = function (tool) {
		this.items.push(tool)
		return tool
	}
}

/**
 * @example
 * var Chart1 = new Tee.Chart("canvas");
 * Chart1.addSeries(new Tee.Bar([1,2,3,4]));
 * Chart1.draw();
 * @constructor
 * @class The main Chart class
 * @param {String|HTMLCanvasElement} [canvas] Optional canvas id or <a href="https://www.w3.org/wiki/HTML/Elements/canvas">element</a>.
 * @property {HTMLCanvasElement} canvas The <a href="https://www.w3.org/wiki/HTML/Elements/canvas">canvas</a> where this chart will paint to.
 * @property {Rectangle} bounds The rectangle where this chart will be painted inside canvas.
 * @property {Tee.Palette} palette The list of colors to use as default colors for series and points.
 * @property {Tee.Chart.Aspect} aspect Contains properties related to 3D and graphics parameters.
 * @property {Tee.Chart.Panel} panel Contains properties used to fill the chart background.
 * @property {Tee.Chart.Walls} walls Contains properties used to draw chart walls around axes.
 * @property {Tee.Chart.Axes} axes Contains a list of axis used to draw series.
 * @property {Tee.Chart.Legend} legend Contains properties to control the legend, a panel showing the list of series or values.
 * @property {Tee.Chart.SeriesList} series Contains a list of Tee.Series objects that belong to this chart.
 * @property {Tee.Chart.Title} title Properties to draw text at top side of chart.
 * @property {Tee.Chart.Title} footer Properties to draw text at bottom side of chart.
 * @property {Tee.Chart.Zoom} zoom Properties to control mouse/touch dragging to zoom chart axes scales.
 * @property {Tee.Chart.Scroll} scroll Properties to control mouse/touch dragging to scroll or pan contents inside chart axes.
 * @property {Tee.Chart.Tools} tools Contains a list of Tool objects that belong to this chart.
 */
let Chart = function (canvas, data, type) {
	var ua = typeof navigator != "undefined" ? navigator.userAgent.toLowerCase() : ""
	/**
	 * @constant
	 * @private
	 */
	this.isChrome = ua.indexOf("chrome") > -1
	/**
	 * @constant
	 * @private
	 */
	this.isAndroid = ua.indexOf("android") > -1
	/**
	 * @constant
	 * @private
	 */
	this.isMozilla = typeof window !== "undefined" && window.mozRequestAnimationFrame

	this.language = window.navigator.userLanguage || window.navigator.language

	if (canvas) {
		if (typeof HTMLCanvasElement !== "undefined" && canvas instanceof HTMLCanvasElement) this.canvas = canvas
		else if (typeof canvas == "string") this.canvas = document.getElementById(canvas)
		else this.canvas = canvas
	}

	if (!this.canvas) {
		this.canvas = document.createElement("canvas")
		this.canvas.width = 600
		this.canvas.height = 400
	}

	var c = this.canvas
	var isNLc = false

	this.__webgl = c.__webgl

	if (c.__webgl || c.clientWidth === 0) this.bounds = new Rectangle(0, 0, c.width, c.height)
	else {
		this.bounds = new Rectangle(0, 0, c.clientWidth, c.clientHeight)
		c.width = c.clientWidth
		c.height = c.clientHeight
	}

	this.chartRect = new Rectangle()
	this.chartRect.automatic = true
	this.chartRect.setFrom(this.bounds)

	this.palette = new Palette([
		"#4466a3",
		"#f39c35",
		"#f14c14",
		"#4e97a8",
		"#2b406b",
		"#1d7b63",
		"#b3080e",
		"#f2c05d",
		"#5db79e",
		"#707070",
		"#f3ea8d",
		"#b4b4b4"
	])

	/**
	 * @memberOf Tee.Chart
	 * @class Contains properties related to canvas and 2D / 3D
	 * @param {Tee.Chart} chart The parent chart this aspect object belongs to.
	 * @property {Boolean} clip When true, series contents will be restricted to paint inside axes boundaries.
	 */
	this.aspect = {
		chart: this,
		view3d: this.__webgl,
		ortogonal: !this.__webgl,
		rotation: 0,
		elevation: 315,
		perspective: 50,
		clip: true,

		_orthox: 10,
		_orthoy: 8,

		/**
		 * @param {Rectangle} r The rectangle object to apply clipping.
		 */
		clipRect: function (r) {
			var c = this.chart.ctx
			c.save()

			c.beginPath()

			if (this.view3d) c.rect(r.x, r.y - this._orthoy, r.width + this._orthox, r.height + this._orthoy)
			else c.rect(r.x, r.y, r.width, r.height)

			c.clip()
			//c.closePath();
		}
	}

	var aspect = this.aspect

	/*
		* Properties to paint the chart background
		*/
	this.panel = new Panel(this)

	/**
	 * @memberOf Tee.Chart
	 * @constructor
	 * @class Contains left, right, bottom and back wall objects
	 * @param {Tee.Chart} chart The parent chart this walls object belongs to.
	 * @property {Boolean} [visible=true] Determines if walls will be displayed or not.
	 * @property {Tee.Chart.Wall} back Visual properties to paint the back wall.
	 */
	this.walls = {
		chart: this,
		visible: true,
		left: new Wall(this),
		right: new Wall(this),
		bottom: new Wall(this),
		back: new Wall(this),

		draw: function (r, aspect) {
			var old,
				ctx = this.chart.ctx,
				t = this.transparency,
				groups = ctx.beginParent

			this.visual = groups ? ctx.beginParent() : null

			if (t > 0) {
				old = ctx.globalAlpha
				ctx.globalAlpha = (1 - t) * old
			}

			var backBounds = this.back.bounds
			backBounds.setFrom(r)

			if (aspect.view3d) {
				var bottomsize = this.bottom.visible ? this.bottom.size : 0,
					leftsize = this.left.size

				if (leftsize > 0) {
					backBounds.x -= leftsize
					backBounds.width += leftsize
				}

				if (bottomsize > 0) backBounds.height += bottomsize

				this.left.bounds.set(r.x - leftsize, r.y, leftsize, r.height + bottomsize)
				this.bottom.bounds.set(r.x, r.getBottom(), r.width, bottomsize)

				this.back.format.depth = this.back.size
			}

			!this.back.visible || this.back.draw()

			if (this.chart.aspect.view3d) {
				if (this.left.visible) this.left.draw()
				if (this.bottom.visible) this.bottom.draw()
				if (this.right.visible) this.right.draw()
			}

			if (t > 0) ctx.globalAlpha = old

			if (groups) ctx.endParent()
		}
	}

	var bf = this.walls.back.format
	bf.fill = "rgb(240,240,240)"
	bf.shadow.visible = true
	bf.z = 1

	var lw = this.walls.left
	lw.format.fill = "#BBAA77"
	lw.format.depth = 1
	lw.size = 2

	var bof = this.walls.bottom
	bof.format.depth = 1
	bof.size = 2

	this.walls.right.visible = false

	/*
		* Four axes
		*/
	this.axes = new Axes(this)

	/*
		* Properties to paint a list of series or values.
		*/
	this.legend = new Legend(this)

	/*
		* List of Tee.Series objects that this chart contains.
		*/
	this.series = new SeriesList(this)

	this.title = new Title(this, "blue")
	this.title.text = "TeeChart"
	this.title.format.z = 1

	this.subtitle = new Title(this, "blue")
	this.subtitle.format.z = 1

	this.footer = new Title(this, "red")
	this.footer.format.z = 0

	this.subfooter = new Title(this, "red")
	this.subfooter.format.z = 0

	this.zoom = new Zoom(this)
	this.scroll = new Scroll(this)

	this.tools = new Tools(this)

	/**
	 * @private
	 */
	this.oldPos = new Point()

	/**
	 * @private
	 * @returns {Point} Returns the xy local coordinates from a mouse or touch event
	 */
	this.calcMouse = function (e, p) {
		p.x = e.clientX
		p.y = e.clientY

		var element = this.canvas,
			r

		// IE, Moz3+, Chr, Op9.5+, Saf4+
		if (element.getBoundingClientRect) {
			r = element.getBoundingClientRect()
			p.x -= r.left
			p.y -= r.top
		} //earlier Moz.
		else if (element.offsetParent)
			do {
				p.x -= element.offsetLeft
				p.y -= element.offsetTop
				element = element.offsetParent
			} while (element)
	}

	var pMove = new Point(0, 0)

	this.domousemove = function (event) {
		var c = this.chart
		if (!c.ctx) return false

		event = event || window.event

		if (event.touches) event = event.touches[event.touches.length - 1]

		c.calcMouse(event, pMove)

		if (c.scroll.active) {
			var d = c.scroll.direction,
				both = d == "both",
				delta

			if (both || d == "horizontal") {
				delta = c.axes.bottom.fromSize(c.oldPos.x - pMove.x)
				c.axes.top.scroll(delta)
				c.axes.bottom.scroll(delta)
			}

			if (both || d == "vertical") {
				delta = -c.axes.left.fromSize(c.oldPos.y - pMove.y)
				c.axes.left.scroll(delta)
				c.axes.right.scroll(delta)
			}

			c.oldPos.x = pMove.x
			c.oldPos.y = pMove.y

			c.scroll.done = true

			if (c.onscroll) c.onscroll(event)

			if (c.scroll.done)
				requestAnimationFrame(() => {
					c.draw()
				})

			return false
		} else if (c.zoom.active) {
			if (pMove.x != c.oldPos.x || pMove.y != c.oldPos.y) {
				c.zoom.change(pMove)

				requestAnimationFrame(() => {
					c.draw()
				})

				c.zoom.done = true
			}

			return false
		} else {
			c.newCursor = null
			c.tools.mousemove(pMove)
			c.series.mousemove(pMove)
			c.legend.mousemove(pMove)
			c.title.mousemove(pMove)
			if (c.mousemove) c.mousemove(pMove)

			var s = this.chart.canvas.style

			if (c.newCursor) {
				if (s.cursor != c.newCursor) {
					c.oldCursor = s.cursor
					s.cursor = c.newCursor
				}
			} else if (c.oldCursor !== undefined && s.cursor != c.oldCursor) s.cursor = c.oldCursor

			return true
		}
	}

	var p = new Point(0, 0)

	this.domousedown = function (event) {
		event = event || window.event
		var done = false,
			c = this.chart

		c.calcMouse(event.touches ? event.touches[0] : event, p)
		var inRect = c.series.anyUsesAxes() && c.chartRect.contains(p)

		doubleTap(c)
		if (c.zoom.enabled) {
			twoFingersZoom(c, c.zoom)
		}

		var inRect = c.series.anyUsesAxes() && c.chartRect.contains(p)

		if (event.touches) {
			//alert("touch! "+event.touches.length.toString());
			// two-finger pinch to zoom, one finger to scroll

			if (event.touches.length > 1) {
				// c.zoom.active=c.zoom.enabled && inRect;
				//if (c.zoom.active)
				c.scroll.active = false
			} else {
				c.scroll.active = c.scroll.enabled && inRect
				if (c.scroll.active) c.zoom.active = false
			}
		} else {
			c.zoom.active = event.button == c.zoom.mouseButton && c.zoom.enabled && inRect
			c.scroll.active = event.button == c.scroll.mouseButton && c.scroll.enabled && inRect
		}

		c.zoom.done = false
		c.scroll.done = false
		c.oldPos = p

		if (event.button === 0) {
			// c.zoom.mouseButton)
			done = c.tools.mousedown(event)
			if (!done) {
				done = c.series.mousedown(event)

				if (!done) done = c.legend.mousedown(event)
			}
			if (!done) if (c.mousedown) done = c.mousedown(event)

			c.canvas.oncontextmenu = null
		} else if (event.button == 2) c.canvas.oncontextmenu = () => false

		if (done) c.zoom.active = c.scroll.active = false
		else done = c.zoom.active || c.scroll.active

		if (event.preventDefault) event.preventDefault()
		else event.cancelBubble = true

		if (done) {
			// IE < 9 : "fromElement"
			var target = event.target || event.fromElement

			if (target && target.setPointerCapture && event.pointerId) target.setPointerCapture(event.pointerId)
		}

		return !done
	}

	this.domouseup = function (event) {
		event = event || window.event
		var c = this.chart,
			done
		c.zoom.active = false
		c.scroll.active = false

		if (c.zoom.done && !c.zoom.touching) {
			if (c.zoom.apply()) if (c.onzoom) c.onzoom()

			c.draw()

			done = true
		} else {
			done = c.scroll.done
			if (!done) {
				done = c.series.clicked(c.oldPos)
				if (!done) {
					done = c.tools.clicked(c.oldPos)
					if (!done) {
						done = c.title.clicked(c.oldPos)
						if (done && c.title.onclick) c.title.onclick(c.title)
					}
				}
				if (!done) if (c.mouseup) done = c.mouseup(event)
			}
		}

		c.zoom.old = null

		c.zoom.done = false
		c.scroll.done = false

		if (done)
			if (event.preventDefault) event.preventDefault()
			else event.cancelBubble = true
		else c.canvas.oncontextmenu = null

		// IE < 9 : "fromElement"
		var target = event.target || event.fromElement

		if (target && target.releasePointerCapture && event.pointerId) target.releasePointerCapture(event.pointerId)
	}

	/*
if (c.addEventListener) {
	c.addEventListener("mousedown", this.domousedown, false);
	c.addEventListener("touchstart", this.domousedown, false);
	c.addEventListener("mousemove", this.domousemove, false);
	c.addEventListener("touchmove", this.domousemove, false);
	c.addEventListener("mouseup", this.domouseup, false);
	c.addEventListener("touchstop", this.domouseup, false);
}
else {
	// IE <9 attachEvent

	if (c.attachEvent) {
		c.attachEvent("onmousedown", this.domousedown);
		c.attachEvent("ontouchstart", this.domousedown);
		c.attachEvent("onmousemove", this.domousemove);
		c.attachEvent("ontouchmove", this.domousedown);
		c.attachEvent("onmouseup", this.domouseup);
		c.attachEvent("ontouchstop", this.domousedown);
	}
}
*/

	c.onpointerdown = c.ontouchstart = this.domousedown
	c.onpointerup = c.ontouchend = this.domouseup
	c.onpointermove = c.ontouchmove = this.domousemove

	// Mouse wheel default to zoom / unzoom axes:

	this._doWheel = function (event) {
		function applyAxis(a) {
			var axisrange = a.maximum - a.minimum

			if (axisrange > 0) {
				var range = delta * axisrange * 0.05
				a.setMinMax(a.minimum + range, a.maximum - range)
			}
		}

		var c = this.chart

		if (!c.zoom.wheel.enabled) {
			for (var t = 0, s, len = c.tools.items.length; t < len; t++) {
				s = c.tools.items[t]
				if (!s) break
				if (s instanceof Tee.ToolTip && s.active) s.hide()
			}

			return
		}

		event = event || window.event

		var delta =
			c.zoom.wheel.factor * (event.wheelDelta ? event.wheelDelta / 120 : event.detail ? -event.detail / 3 : 0)

		if (Math.abs(delta) > 0) {
			var p = { x: 0, y: 0 }
			c.calcMouse(event, p)

			if (c.chartRect.contains(p)) {
				c.axes.each(function () {
					applyAxis(this)
				})

				c.draw()

				event.returnValue = false
				if (event.preventDefault) event.preventDefault()
			}

			for (var t = 0, s, len = c.tools.items.length; t < len; t++) {
				s = c.tools.items[t]
				if (!s) break
				if (s instanceof Tee.ToolTip && s.active) s.mousemove(p)
			}
		}
	}

	if (c.addEventListener) c.addEventListener("DOMMouseScroll", this._doWheel, false)

	c.onmousewheel = this._doWheel

	// Alternative to canvas lack of mouse setCapture:

	c.onmouseout = function (e) {
		if (e && !e.target.setCapture) this.chart.scroll.active = false

		this.chart.series.mouseout()
		this.chart.tools.mouseout()
	}

	/**
	 * @returns {Tee.Series} Returns the series parameter
	 * @param {Tee.Series} series The series object to add to chart.
	 */
	this.addSeries = function (series) {
		series.setChart(series, this)
		if (series.donutArray != null && series.donutArray !== "undefined") series.linkDonutsToChart()

		var li = this.series.items,
			n = li.indexOf(series)

		if (n == -1) n = li.push(series) - 1

		if (series.title === "") series.title = "Series" + (1 + n).toString()

		if (series.format.fill === "") series.format.fill = this.palette.get(n)

		return series
	}

	this.removeSeries = function (series) {
		var li = this.series.items,
			n = li.indexOf(series)
		if (li != -1) li.splice(n, 1)
	}

	c.chart = this

	function newSeries(v) {
		var St = type || Tee.Bar,
			s = c.chart.addSeries(new St(c.chart))
		s.data.values = v
	}

	if (data && data.length > 0) {
		if (data[0] instanceof Array) for (var t = 0; t < data.length; t++) newSeries(data[t])
		else newSeries(data)
	}

	/**
	 * @returns {Tee.Series} Returns the index'th series in chart series list
	 * @param {Integer} index The index of the chart series list to obtain.
	 */
	this.getSeries = function (index) {
		return this.series.items[index]
	}

	/**
	 * Main Chart draw method. Repaints all chart contents.
	 */
	this.draw = function (ctx) {
		var series = this.series,
			r = this.chartRect

		this.ctx = ctx || (this.canvas.getContext ? this.canvas.getContext("2d") : null) //,{alpha:false} (opaque canvas)
		ctx = this.ctx

		if (!ctx) throw "Canvas does not provide Context"

		if (Tee.Scroller && this instanceof Tee.Scroller) {
			ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
		}

		if (r.automatic) r.setFrom(this.bounds)

		var len = series.items.length,
			s,
			paintAxes = false,
			paintWalls = false,
			maxZ = 1,
			visCount = series.visibleCount(),
			tmp = 0

		while (len--) {
			s = series.items[len]

			if (s.visible) {
				s.initZ(tmp, visCount)
				tmp++

				if (s._paintAxes) paintAxes = true
				if (s._paintWalls) paintWalls = true

				if (s.maxZ && s.maxZ > maxZ) maxZ = s.maxZ
			}
		}

		this.walls.left.format.depth = maxZ
		this.walls.bottom.format.depth = maxZ
		this.walls.back.format.z = maxZ

		this.panel.draw()

		if (r.automatic) this.panel.margins.apply(r)

		this.title.tryDraw(true)
		this.subtitle.tryDraw(true)
		this.footer.tryDraw(false)
		this.subfooter.tryDraw(false)

		series.beforeDraw()

		if (this.legend.visible) {
			this.legend.calcrect()
			this.legend.draw()
		}

		if (aspect.view3d && !this.__webgl) {
			r.y += aspect._orthoy
			r.height -= aspect._orthoy
			r.width -= aspect._orthox
		}

		var ax = this.axes,
			oldt

		if (this.series.anyUsesAxes()) {
			ax.each(Axis.adjustRect)
			ax.each(Axis.calcRect)

			if (this.walls.visible && paintWalls) this.walls.draw(r, this.aspect)

			if (ax.visible && paintAxes) {
				if (ax.transparency > 0) {
					oldt = ctx.globalAlpha
					ctx.globalAlpha = (1 - ax.transparency) * oldt
				}

				var groups = ctx.beginParent
				ax.visual = groups ? ctx.beginParent() : null

				ax.each(Axis.draw)

				if (groups) ctx.endParent()

				if (ax.transparency > 0) ctx.globalAlpha = oldt
			}
		}

		this.series.draw()
		this.tools.draw()

		if (this.zoom.active && !this.zoom.touching) this.zoom.draw()

		if (this.ondraw) this.ondraw(this)

		this.isNLc = false
		if (this.isNLc) {
			var f = new Format(this)
			var msg = "TeeChart Evaluation (c)Steema Software 2021"
			var tw
			var ctx = this.ctx
			ctx.save()
			ctx.font = "13px Courier"
			tw = f.textWidth(msg)
			var rText = {
				x: this.chartRect.x + this.chartRect.width / 2 - tw / 2,
				y: this.bounds.height / 2,
				width: tw,
				height: f.textHeight("H")
			}
			ctx.translate(this.chartRect.x + this.chartRect.width / 2, this.bounds.height / 2)
			ctx.rotate((-30 * Math.PI) / 180)
			ctx.translate(-(this.chartRect.x + this.chartRect.width / 2), -(this.bounds.height / 2))
			f.font.fill = "rgba(64,108,128, 0.55)"
			f.drawText(rText, msg)
			ctx.restore()
		}
	}

	/**
	 * Paints chart to image parameter, as PNG or JPEG picture made from canvas.
	 * @param {String} image The id of an Image HTML component.
	 * @param {String} format Can be "image/png" or "image/jpeg"
	 * @param {Number} quality From 0% to 100%, jpeg compression quality.
	 */
	this.toImage = function (image, format, quality) {
		var i = document.getElementById(image)
		if (i) i.src = format !== "" ? this.canvas.toDataURL(format, quality) : this.canvas.toDataURL()
	}
}

Chart.prototype.drawReflection = function () {
	var c = this.ctx,
		h = this.bounds.height

	c.scale(1, -1)
	c.translate(0, -h * 2)

	this.ondraw = null
	this.draw()
	c.translate(0, h * 2)
	c.scale(1, -1)

	var mirrorHeight = this.canvas.height - h,
		y = h,
		gradient = c.createLinearGradient(0, y, 0, y + mirrorHeight),
		color = this.reflectionColor

	gradient.addColorStop(0, colorAlpha(color, 0.5))
	gradient.addColorStop(1, colorAlpha(color, 1))
	c.fillStyle = gradient
	c.beginPath()
	c.shadowColor = "transparent"
	c.rect(0, y, this.bounds.width, mirrorHeight)
	c.fill()

	this.ondraw = this.drawReflection
}

function colorAlpha(color, alpha) {
	return "rgba( " + color[0] + ", " + color[1] + ", " + color[2] + ", " + alpha + " )"
}

function cellText(cell) {
  return cell.textContent || cell.innerText; // IE
}

Chart.prototype.fromTable = function(table, style, cols, header, labels) {
  function setTitleFooter(chart,cell) {
    if (cell) {

      var tmp = cellText(cell);

      if (chart.title.text==="")
        chart.title.text=tmp;
      else
        chart.footer.text=tmp;
    }
  }

  var h, st, c, r, l, row, col, cell,
      ta=(table instanceof HTMLTableElement) ? table : document.getElementById(table);

  if (ta) {
    if (header===null) header=-1;
    if (labels===null) labels=-1;

    this.series.items=[];

    if (cols) {

      if (header>-1) {
         h=ta.rows[header];

         if (h) {
           for (c=labels+1; cell=h.cells[c]; c++) {
             this.addSeries(new style()).title= cell ? cellText(cell) : "Series "+c.toFixed(0);
           }

           if (labels>-1) setTitleFooter(this,h.cells[labels]);
         }
      }
      else
      {
         h=ta.rows[0];

         if (h) {
           for (c=labels+1; cell=h.cells[c]; c++) {
             this.addSeries(new style()).title= "Series "+c.toFixed(0);
           }
         }
      }

      for (r=header+1; row = ta.rows[r]; r++) {
         if (row) {
           for (c=labels+1; col = row.cells[c]; c++) {

               st = ( cellText(col)==="" ) ? null : parseFloat( cellText(col) );
               this.series.items[c-labels-1].data.values.push(st);
           }

           if (labels>-1) {
             l = cellText(row.cells[labels]);
             this.series.each(function(s) { s.data.labels.push(l); } );
           }
         }
      }
    }
    else
    {
      for (r=header+1; row = ta.rows[r]; r++) {
         if (row)
         for (c=labels; col = row.cells[c]; c++) {
           if (c==labels) {
             this.addSeries(new style()).title= col ? cellText(col) : "Series "+r.toFixed(0);
           }
           else {
             st = ( cellText(col) === "") ? null : parseFloat( cellText(col) );
             this.series.items[r-header-1].data.values.push(st);
           }
         }
      }

      if (header>-1) {
        row=ta.rows[header];

        for (c=labels+1; col = row.cells[c]; c++)
          this.series.each(function(s) { s.data.labels.push( cellText(col) ); });

        if (labels>-1)
          setTitleFooter(this,row.cells[labels]);
      }
    }

    this.draw();
  }
}

export { Chart }
